import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useMutation, useQuery } from 'react-apollo'

import {
  FORM_MESSAGES,
  FORM_VALUES,
  PIN_RULES
} from '../../../../constants'
import { getGQLErrorMsg } from '../../../../lib/apollo'
import { useMutateAndLoading } from '../../../../hooks/use-mutate-and-loading'
import Form from '../../../forms/layout-form'
import Row from '../../../forms/layout-row'
import Col from '../../../forms/layout-column'
import PinInput from '../../../forms/pin-input'
import Button from '../../../button'
import { CardWrapper, CardContent, CardInnerHeading } from '../../../shared/settings/card'
import ACCOUNT_QUERY from '../../../../../graphql/queries/account.gql'
import ACCOUNT_MUTATION from '../../../../../graphql/mutations/account.gql'
import {
  PIN_HEADER,
  PIN_SUBHEADER_NO_PIN,
  PIN_SUBHEADER_HAS_PIN,
  PIN_SUBHEADER_EDITABLE,
  PIN_CTA_NO_PIN,
  PIN_CTA_HAS_PIN,
  PIN_HELP_TEXT
} from '../constants'

import styles from './pin-form.css'

const getPinSubHeader = (hasPin, displayForm) => {
  if (displayForm) {
    return PIN_SUBHEADER_EDITABLE
  }

  return hasPin ? PIN_SUBHEADER_HAS_PIN : PIN_SUBHEADER_NO_PIN
}

export default function PinForm() {
  const [displayForm, setDisplayForm] = useState(false)
  const [formError, setFormError] = useState('')
  const {
    data: accountData,
    loading: accountLoading,
    error: accountError
  } = useQuery(ACCOUNT_QUERY)
  const [updateAccount] = useMutation(ACCOUNT_MUTATION)
  const mutateAndLoading = useMutateAndLoading(updateAccount)
  const hasPin = accountData?.account?.hasPin
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: {
      errors,
      touchedFields,
      isSubmitted,
      isSubmitting
    }
  } = useForm({
    defaultValues: {
      pin: '',
      pinConfirmation: ''
    },
    mode: 'onChange'
  })

  if (accountLoading || accountError) {
    return null
  }

  const onSubmit = async values => {
    setFormError('')

    try {
      await mutateAndLoading('pinSetup', {
        variables: {
          input: {
            pin: values.pin
          }
        },
        update: (proxy) => {
          const data = proxy.readQuery({ query: ACCOUNT_QUERY })

          proxy.writeQuery({
            query: ACCOUNT_QUERY,
            data: {
              ...data,
              account: {
                ...data.account,
                hasPin: true
              }
            }
          })
        }
      })
      reset()
      setDisplayForm(false)
    } catch (error) {
      setFormError(getGQLErrorMsg(error))
    }
  }

  const cancelEdit = (event) => {
    event.preventDefault()
    reset()
    setFormError('')
    setDisplayForm(false)
  }

  const showWarning = (field, value) => (value || touchedFields[field] || isSubmitted) &&
    errors[field]?.message

  const cta = hasPin ? PIN_CTA_HAS_PIN : PIN_CTA_NO_PIN
  const subHeader = getPinSubHeader(hasPin, displayForm)

  return (
    <CardWrapper>
      <CardContent className={styles.cardContent}>
        <CardInnerHeading
          header={PIN_HEADER}
          subHeader={subHeader}
        />
        {!displayForm && (
          <Button
            className={styles.setupPinButton}
            text={cta}
            onClick={(event) => {
              event.preventDefault()
              setDisplayForm(true)
            }}
            lightBGPrimary
          />
        )}
        {displayForm && (
          <Form
            handleSubmit={handleSubmit(onSubmit)}
            isSubmitting={isSubmitting}
            formClassName={styles.formClass}
          >
            <Row>
              <Col>
                <Controller
                  name="pin"
                  control={control}
                  rules={PIN_RULES}
                  render={({ field }) => (
                    <PinInput
                      label={`${hasPin ? 'Change' : 'Enter new'} PIN`}
                      warning={showWarning(field.name, field.value)}
                      minLength="0"
                      maxLength={`${FORM_VALUES.pin.length}`}
                      {...field}
                    />
                  )}
                />
                {!errors.pin && getValues('pin') && (
                  <span className={styles.enterPinHelpText}>
                    {PIN_HELP_TEXT}
                  </span>
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name="pinConfirmation"
                  control={control}
                  rules={{
                    ...PIN_RULES,
                    validate: value => value === getValues('pin') ||
                      FORM_MESSAGES.pin.match
                  }}
                  render={({ field }) => (
                    <PinInput
                      label={`${hasPin ? 'Confirm' : 'Confirm new'} PIN`}
                      warning={showWarning(field.name, field.value)}
                      isValid={Boolean(
                        field.value &&
                        !errors.pinConfirmation
                      )}
                      minLength="0"
                      maxLength={`${FORM_VALUES.pin.length}`}
                      {...field}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Button
                  lightBGPrimary
                  className={styles.submitButton}
                  disabled={isSubmitting}
                  text="Save"
                  error={formError}
                />
              </Col>
              <Col>
                <Button
                  secondary
                  text="Cancel"
                  className={styles.cancelButton}
                  disabled={isSubmitting}
                  onClick={cancelEdit}
                />
              </Col>
            </Row>
          </Form>
        )}
      </CardContent>
    </CardWrapper>
  )
}
